import clsx from 'clsx';
import { memo, useCallback, useMemo, useRef, type KeyboardEvent } from 'react';

import type { SegmentedControlProps } from './types';

import { Button } from '../Button/Button';
import { Layout } from '../Layout/Layout';
import './SegmentedControl.css';
import { getSegmentMerge, resolveSegmentIcon } from './utils';

export const SegmentedControl = memo(function SegmentedControl(
  props: SegmentedControlProps,
) {
  const {
    label,
    segments,
    disabled = false,
    fullWidth = false,
    size = 'm',
  } = props;
  const isMultiple = props.selectionMode === 'multiple';
  const segmentRefs = useRef<(HTMLElement | null)[]>([]);

  const selectedKeys = useMemo(
    () =>
      new Set<string>(
        props.selectionMode === 'multiple' ? props.value : [props.value],
      ),
    [props.selectionMode, props.value],
  );

  const handleToggle = useCallback(
    (segmentKey: string, isSelected: boolean) => {
      if (props.selectionMode === 'multiple') {
        const updatedKeys = new Set(props.value);
        if (isSelected) {
          updatedKeys.add(segmentKey);
        } else {
          updatedKeys.delete(segmentKey);
        }
        props.onChange(
          segments
            .map((segment) => segment.segmentKey)
            .filter((key) => updatedKeys.has(key)),
        );
        return;
      }
      // Single selection always keeps one segment on, so turning the current
      // one off is a no-op rather than an empty value.
      if (!isSelected) return;
      props.onChange(segmentKey);
    },
    [props, segments],
  );

  const handleKeyDown = useCallback(
    (event: KeyboardEvent<HTMLElement>, index: number) => {
      const { key } = event;
      const focusableIndexes = segments.reduce<number[]>(
        (indexes, segment, segmentIndex) => {
          if (!segment.disabled) indexes.push(segmentIndex);
          return indexes;
        },
        [],
      );
      if (focusableIndexes.length === 0) return;

      const position = focusableIndexes.indexOf(index);
      let nextIndex: number | null = null;

      if (key === 'ArrowRight' || key === 'ArrowDown') {
        nextIndex =
          focusableIndexes[(position + 1) % focusableIndexes.length] ?? null;
      } else if (key === 'ArrowLeft' || key === 'ArrowUp') {
        nextIndex =
          focusableIndexes[
            (position - 1 + focusableIndexes.length) % focusableIndexes.length
          ] ?? null;
      } else if (key === 'Home') {
        nextIndex = focusableIndexes[0] ?? null;
      } else if (key === 'End') {
        nextIndex = focusableIndexes[focusableIndexes.length - 1] ?? null;
      }

      if (nextIndex === null) return;

      event.preventDefault();
      segmentRefs.current[nextIndex]?.focus();
      // Single selection follows focus, matching a radio group. Multiple
      // selection moves focus only — Space or Enter toggles the segment.
      if (!isMultiple) {
        handleToggle(segments[nextIndex].segmentKey, true);
      }
    },
    [handleToggle, isMultiple, segments],
  );

  // Roving tabindex: one stop for the whole control, landing on the selected
  // segment so keyboard users start where the current value is.
  const activeIndex = useMemo(() => {
    const selectedIndex = segments.findIndex(
      (segment) => !segment.disabled && selectedKeys.has(segment.segmentKey),
    );
    if (selectedIndex !== -1) return selectedIndex;
    return segments.findIndex((segment) => !segment.disabled);
  }, [segments, selectedKeys]);

  return (
    <Layout
      align="center"
      aria-label={label}
      className={clsx(
        'eds-segmented-control',
        `eds-segmented-control--size-${size}`,
        fullWidth && 'eds-segmented-control--full-width',
      )}
      role="group"
      spacing="none"
    >
      {segments.map((segment, index) => {
        const isSelected = selectedKeys.has(segment.segmentKey);
        const { mergeEnd, mergeStart } = getSegmentMerge(
          index,
          segments,
          selectedKeys,
        );

        return (
          <span
            className={clsx(
              'eds-segmented-control--segment',
              mergeStart && 'eds-segmented-control--segment--merge-start',
              mergeEnd && 'eds-segmented-control--segment--merge-end',
            )}
            key={segment.segmentKey}
          >
            <Button
              ref={(element) => {
                segmentRefs.current[index] = element;
              }}
              disabled={disabled || segment.disabled}
              icon={resolveSegmentIcon(segment)}
              label={segment.label}
              selected={isSelected}
              size={size}
              tabIndex={index === activeIndex ? 0 : -1}
              text={segment.text ?? ''}
              tooltip={segment.tooltip}
              variant="tertiary"
              onKeyDown={(event) => handleKeyDown(event, index)}
              onToggle={(updatedSelected) =>
                handleToggle(segment.segmentKey, updatedSelected)
              }
            />
          </span>
        );
      })}
    </Layout>
  );
});

SegmentedControl.displayName = 'SegmentedControl';
